import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Button, Card } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../DebtorReceiptRecordSlice";
import { fetchDebtorReceiptRecord } from "../DebtorReceiptRecordThunk";
import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";
import {
  fetchCustomers,
  fetchLocations,
  fetchUsers,
} from "../../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const DebtorReceiptRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.debtorReceiptRecord);
  const lookups = useSelector((state) => state.lookups);

  useDefaultDateRange(setFilters, filters, "today");

  // Fetch lookup data on mount
  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchLocations());
    dispatch(fetchUsers());
  }, [dispatch]);

  // Auto run report once defaults are in place
  useEffect(() => {
    if (
      filters.filterType &&
      filters.recordType &&
      filters.fromDate &&
      filters.toDate
    ) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
    dispatch({ type: "debtorReceiptRecord/resetList" });
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    };

    dispatch(fetchDebtorReceiptRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };


  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By Customer":
        return (
          <Col xs={24} sm={12} md={12} lg={4}>
            <label>Search Customer</label>
            <SearchListAutoComplete
              entityType="customer"
              optionsList={lookups.customers.map((c) => ({
                sale_id: c.acc_id,
                display_text: c.acc_name,
              }))}
              onSelect={(obj) => handleFilterChange("customerId", obj?.sale_id)}
              placeholder="Search Customer"
              
            />
          </Col>
        );

      case "By Location":
        return (
          <Col xs={24} sm={12} md={12} lg={4}>
            <label>Search Location</label>
            <SearchListAutoComplete
              optionsList={lookups.locations.map((loc) => ({
                sale_id: loc.location_id,
                display_text: loc.location_name,
              }))}
              onSelect={(obj) => handleFilterChange("locationId", obj?.sale_id)}
              placeholder="Location"
            />
          </Col>
        );

      case "By User":
        return (
          <Col xs={24} sm={12} md={12} lg={4}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name || u.name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="mb-4 shadow-md rounded-xl">
      <Row gutter={16} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={12} lg={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Customer", display_text: "By Customer" },
              // { sale_id: "By Location", display_text: "By Location" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={{
              value: filters.filterType,
              label: filters.filterType,
            }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <Col xs={24} sm={12} md={12} lg={4}>
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "without details", display_text: "Without Details" },
              { sale_id: "with details", display_text: "With Details" },
            ]}
            value={{
              value: filters.recordType,
              label:
                filters.recordType === "with details"
                  ? "With Details"
                  : "Without Details",
            }}
            onSelect={(obj) => handleFilterChange("recordType", obj?.sale_id)}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

       <CommonDateRangeFilter
        filters={filters}
        onChange={(fromDate, toDate) => {
        handleFilterChange("fromDate", fromDate);
        handleFilterChange("toDate", toDate);
        }}
      />

        <Col
          xs={24}
          sm={12}
          md={12}
          lg={4}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Report
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default DebtorReceiptRecordFilters;
